import { SHIP_SYSTEMS } from "@frontier/content/ship-systems";
import {
  equipmentDefinition,
  sameEquipmentKey,
} from "@frontier/content/arsenal";
import type {
  EquipmentDefinition,
  EquipmentKey,
} from "@frontier/content/arsenal";
import { equipmentParameters } from "@frontier/content/equipment-tiers";
import type { EquipmentTier } from "@frontier/content/equipment-tiers";
import type { UpgradePricing } from "./use-upgrade-prices";
import {
  moduleEnergyCost,
  MODULES,
  WEAPONS,
} from "@frontier/content/definitions";
import type { Profile } from "@frontier/protocol/contracts";
import { EquipmentArt, equipmentBackdrop } from "./EquipmentArt";
import { blueprintSources } from "./blueprint-sources";
import { installedEquipmentFocus } from "./equipment-focus";
import {
  mountedCount,
  equipmentContextLabel,
  equipmentCount,
  equipmentInstalled,
  equipmentSelection,
  equippedTier,
  installedEquipmentSlot,
} from "./equipment-selection";
import type { EquipmentContext } from "./equipment-selection";
import { passiveInfo } from "./flight-labels";
import { moduleDescriptions, weaponDescriptions } from "./ui-labels";

interface Props {
  item: EquipmentDefinition;
  tier: EquipmentTier;
  context: EquipmentContext;
  profile: Profile;
  busy: boolean;
  progressionAllowed: boolean;
  upgradePricing: UpgradePricing;
  onSelect: (key: EquipmentKey | null, tier?: EquipmentTier) => Promise<void>;
  onCraft: (key: EquipmentKey) => Promise<void>;
  onDismantle: (key: EquipmentKey, tier: EquipmentTier) => Promise<void>;
  onUpgrade: (key: EquipmentKey, fromTier: EquipmentTier) => Promise<void>;
}

function parameterText(key: EquipmentKey, tier: EquipmentTier): string {
  return equipmentParameters(key, tier).map(parameter => `${parameter.label}: ${Number(parameter.value.toFixed(2)).toLocaleString("ru-RU")} ${parameter.unit}`.trim()).join(" · ");
}

export function EquipmentItemDetail({
  item,
  tier,
  context,
  profile,
  busy,
  progressionAllowed,
  upgradePricing,
  onSelect,
  onCraft,
  onDismantle,
  onUpgrade,
}: Props) {
  const key = item.key;
  const grade = `MK ${"I".repeat(tier + 1)}`;
  const count = equipmentCount(profile.arsenal, key, tier);
  const owned = count > 0;
  const selected = equippedTier(profile, context, key) === tier;
  const installed = equipmentInstalled(profile, key, tier);
  const installedSlot = installedEquipmentSlot(profile, key, tier);
  const compatible =
    equipmentSelection(profile, context, key, tier) !== undefined;
  const otherActive =
    context.kind === "module" && key.kind === "module" && !compatible;
  const otherSlot =
    context.kind === "module" && context.slot === 0 ? "Модуль 2" : "Модуль 1";
  const known = profile.arsenal.knownBlueprints.some((blueprint) =>
    sameEquipmentKey(blueprint, key),
  );
  const sources = blueprintSources(item);
  const sourceHint =
    sources.length > 0
      ? `Чертёж: ${sources.join(" или ")}`
      : key.kind === "module" && key.id === "volley"
        ? "Чертёж за помощь первому транспорту"
        : key.kind === "system" && key.id === "reactor" ? "Сезон: бесплатно на уровне 8. Чертёж для всех — с 22 декабря." : "Чертёж ещё не получен";
  const description =
    key.kind === "system"
      ? SHIP_SYSTEMS[key.id].description
      : key.kind === "weapon"
      ? weaponDescriptions[key.id]
      : key.kind === "module"
        ? moduleDescriptions[key.id]
        : passiveInfo(key.id).description;
  const previous = installedEquipmentFocus(profile, context);
  const previousDefinition = previous
    ? equipmentDefinition(previous.key)
    : null;
  const backdrop = equipmentBackdrop(key);
  const cost =
    tier === 2
      ? null
      : (upgradePricing.prices?.items.find((item) =>
          sameEquipmentKey(item.key, key),
        )?.steps[tier] ?? null);
  const nextTier: EquipmentTier = tier === 0 ? 1 : 2;
  const missingCredits = cost
    ? BigInt(cost.credits) - BigInt(profile.credits)
    : 0n;
  const missingComponents = cost
    ? Math.max(0, cost.components - profile.components)
    : 0;
  const upgradeShortfall = [
    missingCredits > 0n ? `${missingCredits} КР` : "",
    missingComponents > 0 ? `${missingComponents} КОМП.` : "",
  ]
    .filter(Boolean)
    .join(" и ");
  const craftShortfall = Math.max(
    0,
    item.recipeComponents - profile.components,
  );
  const craftReason = !known
    ? sourceHint
    : craftShortfall > 0
      ? `Не хватает ${craftShortfall} КОМП. для изготовления MK I.`
      : null;
  const reserved = mountedCount(profile, key, tier);
  const free = count - reserved;
  const installDisabled = busy || !owned || selected || !compatible || (profile.shipFitting !== null && profile.shipFitting !== undefined && free <= 0);
  const upgradeDisabled =
    busy || !owned || !cost || upgradeShortfall.length > 0;
  const craftDisabled = busy || !known || craftShortfall > 0;
  const dismantleDisabled = busy || !owned || free <= 0;
  const status = selected
    ? `УСТАНОВЛЕНО · ${equipmentContextLabel(context)}`
    : otherActive
      ? "ЗАНЯТ ДРУГОЙ АКТИВНЫЙ СЛОТ"
      : owned
        ? "НА СКЛАДЕ"
        : "ЭТОЙ MK НА СКЛАДЕ НЕТ";

  return (
    <article
      className="fitting-detail"
      data-equipment-kind={key.kind}
      data-equipment-id={key.id}
      data-equipment-tier={tier}
      data-installed={selected}
    >
      <div className="fitting-detail-scroll">
        <header className="fitting-item-hero">
          <span className="fitting-item-art" aria-hidden="true">
            {backdrop && <img src={backdrop} alt="" />}
            <EquipmentArt equipment={key} />
          </span>
          <div className="fitting-item-heading">
            <span className="fitting-item-status">{status}</span>
            <h2>
              {item.name} <span>{grade}</span>
            </h2>
            <small>
              Копий {grade}: {count} · свободно: {Math.max(0, free)}
            </small>
            <small>{key.kind === "module" ? "АКТИВНЫЙ" : key.kind === "weapon" ? "ОРУЖИЕ" : key.kind === "system" ? "СИСТЕМА" : "ПАССИВНЫЙ"}</small>
            {installedSlot && !selected && (
              <small>На корабле: {installedSlot}</small>
            )}
          </div>
        </header>
        <p className="fitting-description">{description}</p>
        <p className="fitting-use-hint">{key.kind === "module"
          ? "Активный модуль: после установки включается кнопкой в полёте. Расходует энергию, затем перезаряжается."
          : key.kind === "weapon" ? "Орудие: автоматически стреляет по выбранной цели в пределах дальности и направления огня."
          : "Пассивное оснащение: работает постоянно, пока установлено на корабле."}</p>
        <p className="fitting-primary-parameter">{parameterText(key, tier)}</p>
        {owned &&
          progressionAllowed &&
          (cost ? (
            <>
              <p className="fitting-upgrade-preview">
                Следующая MK {"I".repeat(nextTier + 1)}:{" "}
                {parameterText(key, nextTier)}
              </p>
              {upgradeShortfall && (
                <p className="fitting-action-reason">
                  Не хватает {upgradeShortfall} для улучшения.
                </p>
              )}
            </>
          ) : tier === 2 ? (
            <p className="fitting-upgrade-maximum">
              МАКСИМУМ · MK III
            </p>
          ) : (
            <p className="fitting-action-reason" role="status">
              {upgradePricing.error ?? "Загружаем стоимость улучшения…"}
              {upgradePricing.error && (
                <button
                  type="button"
                  onClick={() => void upgradePricing.reload()}
                >
                  Повторить загрузку
                </button>
              )}
            </p>
          ))}
        {!owned && progressionAllowed && (
          <p className="fitting-action-reason">
            {!known && <strong>КАК ОТКРЫТЬ ЧЕРТЁЖ: </strong>}
            {craftReason ??
              "Изготовь копию MK I, затем нажми «Установить»."}
          </p>
        )}
        {!owned && !progressionAllowed && (
          <p className="fitting-action-reason">
            Во время вводного вылета доступно только принадлежащее оснащение.
          </p>
        )}
        {key.kind === "module" && (
          <p className="fitting-item-secondary">
            {moduleEnergyCost(key.id, profile.hullId)} ЭН · перезарядка{" "}
            {MODULES[key.id].cooldown} с
          </p>
        )}
        {key.kind === "weapon" && (
          <p className="fitting-item-secondary">
            Дальность {WEAPONS[key.id].range} · выстрел каждые{" "}
            {WEAPONS[key.id].cooldown} с
          </p>
        )}
        <section
          className="fitting-comparison"
          aria-label="Сейчас в выбранном слоте"
        >
          <span className="fitting-comparison-art" aria-hidden="true">
            <EquipmentArt equipment={previous?.key ?? null} />
          </span>
          <div>
            <small>{selected ? "НА КОРАБЛЕ" : previous ? "ЗАМЕНИТ" : "ЗАЙМЁТ"} · {equipmentContextLabel(context)}</small>
            <b>
              {previous && previousDefinition
                ? `${previousDefinition.name} MK ${"I".repeat(previous.tier + 1)}`
                : "Свободно"}
            </b>
            {previous && (
              <span>{parameterText(previous.key, previous.tier)}</span>
            )}
          </div>
        </section>
        {otherActive && (
          <p className="fitting-action-reason">
            Модуль уже установлен: {otherSlot}.
          </p>
        )}
        {!compatible && !otherActive && (
          <p className="fitting-action-reason">
            Предмет не подходит к выбранному слоту.
          </p>
        )}
        {owned && progressionAllowed && (
          <details className="fitting-production">
            <summary>Производство и разбор</summary>
            <p>Новая копия MK I на складе.</p>
            {craftReason && (
              <p className="fitting-source-hint">{craftReason}</p>
            )}
            <div className="fitting-production-actions">
              <button
                type="button"
                data-equipment-action="craft"
                disabled={craftDisabled}
                onClick={() => {
                  if (!craftDisabled) void onCraft(key);
                }}
              >
                ИЗГОТОВИТЬ MK I · {item.recipeComponents} КОМП.
              </button>
              <button
                type="button"
                data-equipment-action="dismantle"
                disabled={dismantleDisabled}
                onClick={() => {
                  if (!dismantleDisabled) void onDismantle(key, tier);
                }}
              >
                РАЗОБРАТЬ {grade} · +{item.dismantleComponents} КОМП.
              </button>
            </div>
            {installed && free <= 0 && (
              <p className="fitting-action-reason">
                Все копии {grade} установлены. Для разбора сними или замени
                предмет.
              </p>
            )}
          </details>
        )}
      </div>
      <div className="fitting-detail-actions">
        {owned && (
          <button
            type="button"
            className="fitting-install"
            data-equipment-action="install"
            disabled={installDisabled}
            onClick={() => {
              if (!installDisabled) void onSelect(key, tier);
            }}
          >
            {selected
              ? "УСТАНОВЛЕНО"
              : `УСТАНОВИТЬ ${grade} · ${equipmentContextLabel(context)}`}
          </button>
        )}
        {owned && progressionAllowed && cost && (
          <button
            type="button"
            className="fitting-upgrade"
            data-equipment-action="upgrade"
            disabled={upgradeDisabled}
            onClick={() => {
              if (!upgradeDisabled) void onUpgrade(key, tier);
            }}
          >
            УЛУЧШИТЬ ДО MK {"I".repeat(nextTier + 1)} · {cost.credits} КР +{" "}
            {cost.components} КОМП.
          </button>
        )}
        {!owned && progressionAllowed && (
          <button
            type="button"
            className="fitting-craft"
            data-equipment-action="craft"
            disabled={craftDisabled}
            onClick={() => {
              if (!craftDisabled) void onCraft(key);
            }}
          >
            ИЗГОТОВИТЬ MK I · {item.recipeComponents} КОМП.
          </button>
        )}
        {busy && (
          <p className="fitting-action-reason" role="status">
            Подтверждаем операцию…
          </p>
        )}
      </div>
    </article>
  );
}
